import {FormattedMessage, useIntl} from "react-intl";
import {Button, Col, Divider, Row, Space} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {
    Condition, HealthInsuranceCompanyResponse,
    Patient
} from "../../api-client";
import '../../assets/css/Dentobook.css';
import {isMobile, isMobileOnly} from 'react-device-detect';
import {
    EnvironmentOutlined,
    EuroOutlined,
    HeartOutlined,
    MedicineBoxOutlined,
    UnorderedListOutlined,
    UserOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import parse from 'html-react-parser';
import {calculateFinancialSummary, INVOICE_STATUS} from "../../utils/calculationUtils";
import {ekatteMessages} from "../../i18n/ekatte";
import {messages} from "../../i18n/health-area";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {allCountries} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {getInvoicesApi, getMedicalStaffApi} from "../../api/apiInstances";
import logger from "../../utils/logger";

dayjs.extend(customParseFormat);

export interface PatientFormProps {
    patient: Patient;
    setEditMode: Function;
}

const PatientForm = ({patient, setEditMode}: PatientFormProps) => {
    const intl = useIntl();
    const {locale, userOrganization} = useContext(DentobookApplicationContext);
    const [illnessTags, setIllnessTags] = useState<Condition[]>([]);
    const [allergyTags, setAllergyTags] = useState<Condition[]>([]);
    const [medicationTags, setMedicationTags] = useState<Condition[]>([]);
    const [isPatientSmokerJsx, setPatientSmokerJsx] = useState(<></>);
    const [paid, setPaid] = useState<number>(0);
    const [due, setDue] = useState<number>(0);
    const [healthInsuranceCompany, setHealthInsuranceCompany] = useState<HealthInsuranceCompanyResponse | undefined>(undefined);
    const getTownDescriptionByKey = (key: string | undefined) => {
        if (typeof key === "undefined") {
            return null;
        }

        const localeMessages: { [key: string]: string } = messages[intl.locale].towns;
        return localeMessages[key] || undefined;
    };

    const getEkatteDescriptionByKey = (key: string | undefined) => {
        const ekatteItem = ekatteMessages.find(item => item.value === key);
        return ekatteItem ? ekatteItem.label : undefined;
    };
    const townDescription = getTownDescriptionByKey(patient.healthArea ?? undefined);
    const ekatteDescription = getEkatteDescriptionByKey(patient.ekatte ?? undefined);
    const medicalApi = getMedicalStaffApi();
    const invoicesApi = getInvoicesApi();


    useEffect(() => {
        if (patient.isSmoker === null) {
            setPatientSmokerJsx(<FormattedMessage id="not_available"/>);
        } else if (patient.isSmoker) {
            setPatientSmokerJsx(<FormattedMessage id="yes_ans"/>);
        } else {
            setPatientSmokerJsx(<FormattedMessage id="no_ans"/>);
        }

        // Calculate financial summary from invoices (source of truth for payments)
        // Includes both regular invoices and proforma invoices
        invoicesApi.fetchInvoicesByPatientId(patient.id!)
            .then(res => {
                const allInvoices = res.data;
                const regularInvoices = allInvoices.filter(inv => inv.documentType === 'INVOICE');
                const proformaInvoices = allInvoices.filter(inv => inv.documentType === 'PROFORMA_INVOICE');

                const financialSummary = calculateFinancialSummary(regularInvoices, proformaInvoices);

                setPaid(financialSummary.paid);
                setDue(financialSummary.due);

                if (patient.healthInsuranceCompanyId) {
                    medicalApi.fetchInsuranceCompanyById(patient.healthInsuranceCompanyId)
                        .then(res => {
                            setHealthInsuranceCompany(res.data);
                        })
                        .catch(err => logger.log(err));
                }
            })
            .catch(err => logger.log(err));
    }, []);


    useEffect(() => {
        if (patient.conditions) {
            setIllnessTags(patient.conditions.filter(cond => cond.type === 'ILLNESS' || cond.type === 'DIAGNOSE'));
            setMedicationTags(patient.conditions.filter(cond => cond.type === 'MEDICATION'));
            setAllergyTags(patient.conditions.filter(cond => cond.type === 'ALLERGY'));
        }
    }, [patient.conditions]);


    const patientYearsOld = patient.birthDate ? dayjs(new Date())
        .diff(patient.birthDate!.toString(), 'year') : 0;

    const countryObject = allCountries.find(country => country.code === patient.country);
    const countryName = locale === 'BG' ? countryObject?.name_bg : countryObject?.name_en;

    // Use presigned R2 URL from API response
    const avatarSrc = patient.avatarUrl;

    return (
        <>
            <h4><span className="text-blue mr-8"><UserOutlined/></span> {<FormattedMessage id="personal_information"/>}
            </h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                {!avatarSrc ? <UserIcon size={isMobileOnly ? "medium" : "large"}/> :
                    <img className="photo-img"
                         width={150}
                         src={avatarSrc}
                         alt="avatar"/>
                }
                <Space direction="vertical" className={isMobileOnly ? "mb-2 mt-16" : "ml-40 mb-2 mt-16"}>
                    <span className="body-large-regular">{<FormattedMessage id="name"/>}:</span>
                    <span className="body-large-medium">
                        {patient.firstName + ' ' + (patient.secondName || '') + ' ' + patient.lastName}
                    </span>
                    <Space direction="vertical" className="mt-24">
                        <span className="body-large-regular">{<FormattedMessage id="birth_date"/>}:</span>
                        <span
                            className="body-large-medium">{patient.birthDate ? dayjs(patient.birthDate).format("DD.MM.YYYY") +
                            ' (' + patientYearsOld + ' ' +
                            intl.formatMessage({id: patientYearsOld !== 1 ? 'years_old' : 'year_old'}) + ')'
                            : <FormattedMessage id="not_available"/>}</span>
                    </Space>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "mb-2 mt-16" : "ml-40 mb-2 mt-16"}>
                    <span className="body-large-regular">{<FormattedMessage id="gender"/>}:</span>
                    <span className="body-large-medium">
                        {patient.gender === 'male' ? <FormattedMessage id="gender_male"/> :
                            patient.gender === 'female' ? <FormattedMessage id="gender_female"/> :
                            <FormattedMessage id="not_available"/>}
                    </span>
                    <Space direction="vertical" className="mt-24">
                        <span className="body-large-regular">
                            {userOrganization?.country === 'BG' ?
                                <FormattedMessage id="egn"/> :
                                <FormattedMessage id="national_id"/>}:
                        </span>
                        <span className="body-large-medium">
                            {patient.egn ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "mb-2 mt-16" : "ml-40 mb-2 mt-16"}>
                    <br/>
                    <br/>
                    <Space direction="vertical" className="mt-24">
                        <span className="body-large-regular">{<FormattedMessage id="profession"/>}:</span>
                        <span className="body-large-medium">
                            {patient.profession ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Space>
            </Space>
            <Divider/>
            <h4>
                <span className="text-blue mr-8"><MedicineBoxOutlined/></span> {<FormattedMessage
                id="general_medical_history"/>}
            </h4>

            <Row gutter={isMobileOnly ? 24 : 0}>
                <Col md={8} className="mt-2">
                    <Space direction="vertical">
                        <span className="body-large-regular">{<FormattedMessage id="illnesses"/>}:</span>
                        <Space direction={illnessTags.length > 2 ? "vertical" : "horizontal"}
                               className={!illnessTags.length ? "body-large-medium" : ""}>{illnessTags.length ?
                            illnessTags.map(t => (
                                <div key={t.id}>
                                    <span className="tagify__tag pl-16 pr-16 p-4 font-weight-700 dentobook-tag"
                                          key={t.id}>{t.title}</span>
                                </div>
                            )) : <FormattedMessage id="not_available"/>}</Space>
                    </Space>
                </Col>
                <Col md={8} className="mt-2">
                    <Space direction="vertical">
                        <span className="body-large-regular">{<FormattedMessage id="medications"/>}:</span>
                        <Space direction={medicationTags.length > 2 ? "vertical" : "horizontal"}
                               className={!medicationTags?.length ? "body-large-medium" : ""}>{medicationTags?.length ?
                            medicationTags.map(t => (
                                <div key={t.id}>
                                    <span className="tagify__tag pr-16 pl-16 p-4 font-weight-700 dentobook-tag"
                                          key={t.id}>{t.title}</span>
                                </div>
                            )) : <FormattedMessage id="not_available"/>}</Space>
                    </Space>
                </Col>
                <Col md={8} className="mt-2">
                    <Space direction="vertical">
                        <span className="body-large-regular">{<FormattedMessage id="allergies"/>}:</span>
                        <Space direction={allergyTags.length > 2 ? "vertical" : "horizontal"}
                               className={!allergyTags?.length ? "body-large-medium" : ""}>{allergyTags?.length ?
                            allergyTags.map(t => (
                                <div key={t.id}>
                                    <span className="tagify__tag pl-16 pr-16 p-4 font-weight-700 dentobook-tag"
                                          key={t.id}>{t.title}</span>
                                </div>
                            )) : <FormattedMessage id="not_available"/>}</Space>
                    </Space>
                </Col>
            </Row>
            <Divider/>
            <h4>
                <span className="text-blue mr-8"><EuroOutlined/></span> {<FormattedMessage id="payments"/>}
            </h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                <Space direction="vertical" size={"middle"}>
                    <span className="body-large-regular">{<FormattedMessage id="paid"/>}:</span>
                    <span className="greenBtn body-large-medium">{paid.toFixed(2).replace(/\.?0+$/, '')} {userOrganization?.currency ?? 'EUR'}</span>
                </Space>
                <Space direction="vertical" size={"middle"} className={isMobileOnly ? "mt-2" : "ml-40"}>
                    <span className="mb-2 body-large-regular">{<FormattedMessage id="obligations"/>}:</span>
                    <span className="redBtn body-large-medium">{due.toFixed(2).replace(/\.?0+$/, '')} {userOrganization?.currency ?? 'EUR'}</span>
                </Space>
            </Space>
            <Divider/>

            <h4>
                <span className="text-blue mr-8"><UnorderedListOutlined/></span> <FormattedMessage id="registration"/>
            </h4>

            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                <Space direction={"vertical"} className="mb-4">
                    <span className="body-large-regular mb-24">{<FormattedMessage id="registeredOn"/>}:</span>
                    <span className="body-large-medium">{dayjs(patient.createdAt).format("DD.MM.YYYY")}</span>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "mt-4" : "ml-40"}>
                    <span className="body-large-regular">{<FormattedMessage id="last_hour"/>}:</span>
                    <span className="body-large-medium">
                        {patient.lastAppointment ? dayjs(patient.lastAppointment).format("DD.MM.YYYY") :
                            <FormattedMessage id="not_available"/>}
                    </span>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "mt-4" : "ml-40"}>
                    <span className="body-large-regular">{<FormattedMessage id="next_hour"/>}:</span>
                    <span className="body-large-medium">
                        {patient.nextAppointment ? dayjs(patient.nextAppointment).format("DD.MM.YYYY") :
                            <FormattedMessage id="not_available"/>}
                    </span>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "mt-4" : "ml-40"}>
                    <span className="body-large-regular">{<FormattedMessage id="doctors"/>}:</span>
                    <span className="body-large-medium">
                        {(patient.treatingDentists ?? []).length > 0
                            ? (patient.treatingDentists ?? []).join(', ')
                            : <FormattedMessage id="not_available"/>}
                    </span>
                </Space>
            </Space>
            <Divider/>
            <h4><span className="text-blue mr-8"><EnvironmentOutlined/></span> {<FormattedMessage id="contact"/>}
            </h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                <Space direction={"vertical"}>
                    <span className="body-large-regular">{<FormattedMessage id="email"/>}:</span>
                    <span className="body-large-medium">{patient.email ? patient.email :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-4" : "ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="phone"/>}:</span>
                    <span className="body-large-medium">{patient.phone ? patient.phone :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-4" : "ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="town"/>}:</span>
                    <span className="body-large-medium">{patient.town ? patient.town :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-4" : "ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="country"/>}:</span>
                    <span className="body-large-medium">{patient.country ?
                        (countryName ?? patient.country) :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                {userOrganization?.country === 'BG' ?
                    <>
                        <Space direction={"vertical"} className={isMobileOnly ? "mt-4" : "ml-56"}>
                            <span className="body-large-regular">{<FormattedMessage id="healthArea"/>}:</span>
                            <span className="body-large-medium">{townDescription ??
                                <FormattedMessage id="not_available"/>}</span>
                        </Space>
                        <Space direction={"vertical"} className={isMobileOnly ? "mt-4" : "ml-56"}>
                            <span className="body-large-regular">{<FormattedMessage id="ekkatte"/>}:</span>
                            <span className="body-large-medium">{ekatteDescription ??
                                <FormattedMessage id="not_available"/>}</span>
                        </Space>
                    </> : null}
            </Space>
            <Row className="mt-40">
                <Col md={24}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular">{<FormattedMessage id="address"/>}:</span>
                        <span className="body-large-medium">
                            {patient.addressOne ? patient.addressOne : <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Col>
            </Row>
            <Row className="mt-24">
                <Col>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular"><FormattedMessage id="preferred_notification_channel"/>:</span>
                        <span className="body-large-medium">
                            {intl.formatMessage({id: `channel_${patient.preferredNotificationChannel || 'email'}`})}
                        </span>
                    </Space>
                </Col>
                <Col style={{marginLeft: 48}}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular"><FormattedMessage id="patient_email_allowed"/>:</span>
                        <span className="body-large-medium">
                            {patient.emailNotificationsEnabled === false
                                ? <FormattedMessage id="no_ans"/>
                                : <FormattedMessage id="yes_ans"/>}
                        </span>
                    </Space>
                </Col>
                <Col style={{marginLeft: 48}}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular"><FormattedMessage id="patient_phone_allowed"/>:</span>
                        <span className="body-large-medium">
                            {(patient as any).phoneNotificationsEnabled === false
                                ? <FormattedMessage id="no_ans"/>
                                : <FormattedMessage id="yes_ans"/>}
                        </span>
                    </Space>
                </Col>
            </Row>
            <Divider/>
            <h4><span className="text-blue mr-8"><HeartOutlined/></span> {<FormattedMessage id="anamensis_title"/>}
            </h4>
            <Space direction={"vertical"}>
                <span className="body-large-regular">{<FormattedMessage id="anesthetic_question2"/>}</span>
                <span className="body-large-regular body-bold font-16">{patient.anesthesia ?
                    <FormattedMessage id="yes_ans"/> : <FormattedMessage id="no_ans"/>}</span>
            </Space>

            <Divider/>
            <h4><span className="text-blue mr-8"><UnorderedListOutlined/></span> {<FormattedMessage
                id="social_anamnezis"/>}
            </h4>
            <Space direction="vertical">
                <Space direction={"vertical"}>
                    <span className="body-large-regular">{<FormattedMessage id="is_health_insured_question"/>}</span>
                    <span className="body-large-regular body-bold font-16">{healthInsuranceCompany ?
                        healthInsuranceCompany.name : <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className="mt-16">
                    <span className="body-large-regular">{<FormattedMessage id="frequency_quesiton"/>}</span>
                    <span className="body-large-regular font-16 body-bold">{patient.visitPeriod ? <FormattedMessage
                        id={patient.visitPeriod?.toLowerCase()}/> : <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className="mt-16">
                    <span className="body-large-regular">{<FormattedMessage id="last_visit_patient_quesiton"/>}</span>
                    {patient.lastVisit ?
                        <span className="body-large-regular body-bold font-16">
                        {patient.lastVisit ? (dayjs(patient.lastVisit).format('DD.MM.YYYY') + ', ' + intl.formatMessage({id: 'reason'}) +
                                ': ' + (patient.lastVisitReason ?? intl.formatMessage({id: 'not_available'}).toLowerCase())) :
                            <FormattedMessage id="not_available"/>}
                    </span>
                        : <span className="body-large-regular body-bold font-16"><FormattedMessage id="not_available"/></span>}
                </Space>
                <Space direction={"vertical"} className="mt-16">
                    <span className="body-large-regular">{<FormattedMessage id="is_patient_afraid_question"/>}</span>
                    <span className="body-large-regular body-bold font-16">{patient.fear ? <FormattedMessage
                        id={patient.fear?.toLowerCase()}/> : <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className="mt-16">
                    <span className="body-large-regular">{<FormattedMessage id="smoker_question"/>}</span>
                    <span className="body-large-regular body-bold font-16">
                        {isPatientSmokerJsx}
                    </span>
                </Space>
            </Space>
            <Divider/>
            <Row className="mt-4 mb-8" style={{marginBottom: isMobile ? 200 : 80}}>
                <Col md={24}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular font-14">{<FormattedMessage id="notes"/>}:</span>
                        <span className="body-large-medium font-16">
                            {patient.notes ? parse(patient.notes || "") : <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Col>
            </Row>
            <FormButtons>
                <Button onClick={() => setEditMode(true)}
                        className={"edit-btn " + (isMobileOnly ? "mobile-update-btn" : "dentobook-btn-primary")}
                        type="primary"
                        size="large" shape="round"><FormattedMessage id="edit"/></Button>
            </FormButtons>
        </>
    );
}

export default PatientForm;